import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from './Button';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { useCredits } from '../../hooks/useCredits';
import { CreditsDisplay } from '../credits/CreditsDisplay';
import { PurchaseModal } from '../credits/PurchaseModal';
import { Gem } from 'lucide-react';
import { COLORS } from '../../constants/colors';
import { CartButton } from '../CartButton';

const HeaderContainer = styled.header`
  display: flex;   
  align-items: center;   
  justify-content: space-between;   
  padding: 16px 24px;   
  background-color: #fff;   
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);   
  position: relative;
`;

const Nav = styled.nav<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 24px;
  
  @media (max-width: 768px) {
    display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: #f8f9fa;
    padding: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-bottom: 2px solid #e9ecef;
    z-index: 100;
    align-items: flex-start;
  }
`;

const NavLink = styled(Link)`
  color: #495057;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: ${COLORS.PRIMARY};
  }

  &.active {
    color: ${COLORS.PRIMARY};
    font-weight: 600;
  }

  @media (max-width: 768px) {
    padding: 8px 0;
    width: 100%;
  }
`;

/*
 * Same look as NavLink, but it opens the price dialog instead of
 * navigating — there is no /prices route.
 */
const NavAction = styled.button`
  display: inline-flex;
  align-items: center;
  color: #495057;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: ${COLORS.PRIMARY};
  }

  @media (max-width: 768px) {
    padding: 8px 0;
    width: 100%;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ProfileImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  cursor: pointer;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const ProfilePlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  color: #495057;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  cursor: pointer;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: #495057;
  padding: 0;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const AuthButtons = styled.div<{ $isOpen: boolean }>`
  display: flex;
  gap: 8px;
  @media (max-width: 768px) {
    display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
    flex-direction: column;
    width: 100%;
    button {
      width: 100%;
    }
  }
`;

const NavIcon = styled.span`
  margin-right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 20px;
  }
`;

const ProfileContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const StyledLogo = styled(Link)`
  display: flex;
  align-items: center;
  height: 100%;
  text-decoration: none;
  
  img {
    height: calc(100% - 1px);
    max-height: 80px;
    object-fit: contain;
    border-radius: 6px;
    transition: all 0.2s ease;
    
    @media (max-width: 768px) {
      height: 60px;
    }
  }
  
  &:hover img {
    opacity: 0.9;
  }
`;

const DesktopCreditsWrapper = styled.div`
  display: block;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileCreditsSection = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    width: 100%;
    padding: 12px 0;
    border-bottom: 1px solid #e9ecef;
    margin-bottom: 12px;
  }
`;

const MobileCreditsContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const CreditsSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  .label {
    color: #6c757d;
    font-size: 14px;
    font-weight: 500;
  }
  
  .amount {
    font-size: 18px;
    font-weight: 700;
    color: #4263eb;
  }
  
  .icon {
    font-size: 20px;
  }
`;

const AddCreditsButton = styled.button`
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

/* Profile section shown inside the burger menu on mobile */
const MobileProfileSection = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 0;
    border-bottom: 1px solid #e9ecef;
    margin-bottom: 12px;
    cursor: pointer;
  }
`;

const MobileProfileName = styled.span`
  font-weight: 600;
  color: #495057;
  font-size: 15px;
`;

export const Header: React.FC = () => {
  const { user, isLoggedIn, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showPricesModal, setShowPricesModal] = useState(false);
  
  const { credits } = useCredits();

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };
  const handleProfileClick = () => {
    navigate('/profile');
    setMenuOpen(false);
  };
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };
  const closeMenu = () => {
    setMenuOpen(false);
  };
  const handleCreditsClick = () => {
    setShowPurchaseModal(true);
    setMenuOpen(false);
  };
  const handlePricesClick = () => {
    setShowPricesModal(true);
    setMenuOpen(false);
  };
  
  return (
    <>
    <HeaderContainer>
      <StyledLogo to="/" onClick={closeMenu}>
        <img 
          src="/assets/images/logo_writing.jpg" 
          alt={t('app.title')} 
        />
      </StyledLogo>
      <RightSection>
        <CartButton />
        {/* Language switcher visible on both mobile and desktop */}
        <LanguageSwitcher variant='compact' />
        <HamburgerButton onClick={toggleMenu}>
          {menuOpen ? '✕' : '☰'}
        </HamburgerButton>

        <Nav $isOpen={menuOpen}>
          {/* Mobile Profile Section - Only visible in burger menu */}
          {isLoggedIn && (
            <MobileProfileSection onClick={handleProfileClick}>
              {user?.profilePicture ? (
                <ProfileImage
                  src={user.profilePicture}
                  alt={user.username}
                />
              ) : (
                <ProfilePlaceholder>
                  {getInitials(user?.username || 'User')}
                </ProfilePlaceholder>
              )}
              <MobileProfileName>{user?.username || 'User'}</MobileProfileName>
            </MobileProfileSection>
          )}

          {/* Mobile Credits Section - Only visible in burger menu */}
          {isLoggedIn && (
            <MobileCreditsSection>
              <MobileCreditsContent>
                <CreditsSummary>
                  <Gem style={{ marginRight: '4px' }} aria-hidden="true" />
                  <span className="label">{t('credits.label')}:</span>
                  <span className="amount">{credits}</span>
                </CreditsSummary>
                <AddCreditsButton onClick={handleCreditsClick}>
                  {t('credits.addMore', 'Add More +')}
                </AddCreditsButton>
              </MobileCreditsContent>
            </MobileCreditsSection>
          )}

          <NavLink to="/" onClick={closeMenu}>
            <NavIcon>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </NavIcon>
            {t('navigation.home')}
          </NavLink>

          <NavLink to="/library" onClick={closeMenu}>
            <NavIcon>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 19.5A2.5 2.5 0 016.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </NavIcon>
            {t('navigation.library')}
          </NavLink>

          {/* Logged-out visitors get a read-only view of the credit prices */}
          {!isLoggedIn && (
            <NavAction onClick={handlePricesClick}>
              <NavIcon>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 1v22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </NavIcon>
              {t('navigation.prices', 'Prices')}
            </NavAction>
          )}

          <NavLink to={`${i18n.language === 'en' ? '' : `/${i18n.language}`}/blog`} onClick={closeMenu}>
            <NavIcon>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M7 7H17M7 12H17M7 17H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </NavIcon>
            {t('navigation.blog')}
          </NavLink>

          {isLoggedIn && (
            <NavLink to="/create" onClick={closeMenu}>
              <NavIcon>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </NavIcon>
              {t('navigation.createStory')}
            </NavLink>
          )}

          {!isLoggedIn && (
            <AuthButtons $isOpen={menuOpen}>
              <Button
                variant="outline"
                size="small"
                onClick={() => {
                  navigate('/login');
                  closeMenu();
                }}
              >
                {t('auth.login')}
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={() => {
                  navigate('/register');
                  closeMenu();
                }}
              >
                {t('auth.signup')}
              </Button>
            </AuthButtons>
          )}

          {/* Logout button inside burger menu on mobile */}
          {isLoggedIn && menuOpen && (
            <AuthButtons $isOpen={menuOpen}>
              <Button
                variant="outline"
                size="small"
                onClick={() => {
                  logout();
                  closeMenu();
                }}
              >
                {t('auth.logout')}
              </Button>
            </AuthButtons>
          )}
        </Nav>

        {/* Desktop-only: credits, profile, logout */}
        <UserSection>
          {isLoggedIn && (
            <DesktopCreditsWrapper>
              <CreditsDisplay 
                credits={credits}
                onPurchaseClick={() => setShowPurchaseModal(true)}
              />
            </DesktopCreditsWrapper>
          )}
          {isLoggedIn && (
            <>
              {user?.profilePicture ? (
                <ProfileContainer>
                  <ProfileImage
                    src={user.profilePicture}
                    alt={user.username}
                    onClick={handleProfileClick}
                  />
                </ProfileContainer>
              ) : (
                <ProfileContainer>
                  <ProfilePlaceholder onClick={handleProfileClick}>
                    {getInitials(user?.username || 'User')}
                  </ProfilePlaceholder>
                </ProfileContainer>
              )}
              <Button
                variant="outline"
                size="small"
                onClick={logout}
              >
                {t('auth.logout')}
              </Button>
            </>
          )}
        </UserSection>
      </RightSection>
    </HeaderContainer>
    {isLoggedIn && (
      <PurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        currentCredits={credits}
      />
    )}
    {!isLoggedIn && (
      <PurchaseModal
        isOpen={showPricesModal}
        onClose={() => setShowPricesModal(false)}
        currentCredits={0}
        viewOnly
      />
    )}
    </>
  );
};